// Redux Utils
import { useDispatch, useSelector } from "react-redux";

// React Utils
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";

// Icons
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// Animations
import { motion, AnimatePresence } from "framer-motion";

// Skeleton
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";

// Components
import Portada from "@/components/Perfil/Portada";
import Tab from "@/components/Tab";
import TicketCarousel from "@/components/TicketCarousel/TicketCarousel";
import TicketCardEventoPublico from "@/components/TicketCarousel/TicketCardEventoPublico";
import TicketCardReferralLink from "@/components/TicketCarousel/TicketCardReferralLink";

// Thunks
import {
  getUserInfo,
  getEventosConTicketsDeUsuario,
  getReferralLinksDeUsuario,
} from "@/features/user/userThunks";
import {
  crearSeguidor,
  eliminarSeguidor,
} from "@/features/seguidores/seguidoresThunks";
import {
  getFavoritosByUsuarioUid,
  getFavoritasExperienciasByUid,
} from "@/features/eventosFavoritos/eventosFavoritosSlice";

// Assets
import genericAvatar from "@/assets/avatars/avatar-generic.webp";

// Utils
import { shareContent } from "@/utils/shareUtil";

// Animation variants
const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, delay: i * 0.08, ease: "easeOut" },
  }),
};

export default function UsuarioPerfil() {
  // Obtener el UID de los parámetros de la ruta
  const { id } = useParams();

  // ========== REFS PARA EVITAR PETICIONES DUPLICADAS ==========
  const hasLoadedBasicData = useRef(false);
  const hasLoadedEventosData = useRef(false);

  // Estados locales para el manejo de la carga individual por sección
  const [userInfoLoaded, setUserInfoLoaded] = useState(false);

  // Estados para las secciones de eventos con tickets
  const [eventosPasados, setEventosPasados] = useState([]);
  const [loadingEventosPasados, setLoadingEventosPasados] = useState(false);

  // Estados para las secciones de links de referido (si es embajador)
  const [referralLinksActivos, setReferralLinksActivos] = useState([]);
  const [referralLinksFinalizados, setReferralLinksFinalizados] = useState([]);
  const [loadingReferralLinks, setLoadingReferralLinks] = useState(false);

  // Estado para controlar qué tab está activa
  const [activeTab, setActiveTab] = useState(0);
  const [tabsDataLoaded, setTabsDataLoaded] = useState({
    eventos: false,
    misTickets: false,
  });

  // Redux state
  const dispatch = useDispatch();
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const {
    info,
    isLoading: userLoading,
    error: userError,
  } = useSelector((state) => state.user);

  // Follow state — initialized from API response after load
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [totalSeguidores, setTotalSeguidores] = useState(0);

  // Favoritos del usuario visitado
  const [favoritosPublicos, setFavoritosPublicos] = useState([]);
  const [experienciasFavoritasPublicas, setExperienciasFavoritasPublicas] = useState([]);
  const [loadingFavoritosPublicos, setLoadingFavoritosPublicos] = useState(false);
  const [favoritosLoaded, setFavoritosLoaded] = useState(false);

  console.log("Rendering UsuarioPerfil with id:", id, "info:", info);
  const now = new Date().toISOString();

  // Build social networks array from user info
  const buildRedesSociales = (userInfo) => {
    const redes = [];
    if (userInfo?.facebook) redes.push({ url: userInfo.facebook, icon: "fa-brands fa-facebook" });
    if (userInfo?.instagram) redes.push({ url: userInfo.instagram, icon: "fa-brands fa-instagram" });
    if (userInfo?.twitter) redes.push({ url: userInfo.twitter, icon: "fa-brands fa-x-twitter" });
    if (userInfo?.tiktok) redes.push({ url: userInfo.tiktok, icon: "fa-brands fa-tiktok" });
    if (userInfo?.youtube) redes.push({ url: userInfo.youtube, icon: "fa-brands fa-youtube" });
    if (userInfo?.twitch) redes.push({ url: userInfo.twitch, icon: "fa-brands fa-twitch" });
    if (userInfo?.spotify) redes.push({ url: userInfo.spotify, icon: "fa-brands fa-spotify" });
    if (userInfo?.linkedin) redes.push({ url: userInfo.linkedin, icon: "fa-brands fa-linkedin" });
    if (userInfo?.whatsapp) redes.push({ url: `https://wa.me/${userInfo.whatsapp.replace(/\D/g, "")}`, icon: "fa-brands fa-whatsapp" });
    if (userInfo?.website) redes.push({ url: userInfo.website, icon: "fa-solid fa-globe" });
    return redes;
  };

  const redesSociales = buildRedesSociales(info);

  // Cargar información básica del usuario al montar
  useEffect(() => {
    const loadBasicData = async () => {
      if (hasLoadedBasicData.current || !id) return;
      hasLoadedBasicData.current = true;

      console.log("🔵 Iniciando carga de información básica del usuario...");

      try {
        // 1. Cargar información del usuario
        console.log("📡 Cargando información del usuario...");
        const userResult = await dispatch(getUserInfo({ uid: id })).unwrap();
        setUserInfoLoaded(true);
        // Initialize follow state from API response
        if (userResult?.isFollowing !== undefined) {
          setIsFollowing(!!userResult.isFollowing);
        }
        if (userResult?.totalSeguidores !== undefined) {
          setTotalSeguidores(userResult.totalSeguidores);
        }
        console.log("✅ Información del usuario cargada");

        // 2. Cargar eventos pasados donde el usuario tiene tickets
        console.log("📡 Cargando eventos pasados...");
        setLoadingEventosPasados(true);
        const eventosResult = await dispatch(
          getEventosConTicketsDeUsuario({
            uid: id,
            filters: {
              eventoFechaHoraFinHasta: now,
              sortBy: "eventoFechaHoraInicio",
              sortOrder: "desc",
              page: 1,
              limit: 10,
            },
          }),
        ).unwrap();

        const eventosFormatted =
          eventosResult?.eventos?.map((evento) => ({
            uid: evento.uid,
            nombre: evento.nombre,
            img: evento.img,
            fechaHoraInicio: evento.fechaHoraInicio,
            fechaHoraFin: evento.fechaHoraFin,
            direccion: evento.direccion,
            zonaHoraria: evento.zonaHoraria,
            published: evento.published,
          })) || [];

        console.log(
          "✅ Eventos pasados obtenidos del backend:",
          eventosFormatted,
        );

        setEventosPasados(eventosFormatted);
        setLoadingEventosPasados(false);
        setTabsDataLoaded((prev) => ({ ...prev, misTickets: true }));
        console.log("✅ Eventos pasados cargados:", eventosFormatted.length);
      } catch (error) {
        console.error("❌ Error al cargar datos básicos:", error);
        setUserInfoLoaded(true);
        setLoadingEventosPasados(false);
      }
    };

    loadBasicData();
  }, [dispatch, id]);

  // Cargar eventos de embajador automáticamente cuando info esté disponible y sea embajador
  useEffect(() => {
    if (userInfoLoaded && info?.perEmbajador === 1 && !tabsDataLoaded.eventos) {
      console.log(
        "🔵 Usuario es embajador, cargando eventos automáticamente...",
      );
      loadEventosData();
    }
  }, [userInfoLoaded, info?.ambassadorActive]);

  // Cargar favoritos automáticamente al montar (Favoritos es siempre el tab 0)
  useEffect(() => {
    if (userInfoLoaded && activeTab === 0) {
      loadFavoritosPublicosData();
    }
  }, [userInfoLoaded]);

  // Función para cargar eventos (links de referido) si es embajador
  const loadEventosData = async () => {
    if (hasLoadedEventosData.current || !info?.ambassadorActive) return;
    hasLoadedEventosData.current = true;

    console.log("🔵 Iniciando carga de links de referido...");
    setLoadingReferralLinks(true);

    try {
      // Cargar links activos
      console.log("📡 Cargando links de referido activos...");
      const resolvedUid = info?.uid || id;
      const activosResult = await dispatch(
        getReferralLinksDeUsuario({
          uid: resolvedUid,
          filters: {
            eventoEstado: "activo",
            eventoFechaHoraInicioDesde: now,
            sortBy: "eventoFechaHoraInicio",
            sortOrder: "asc",
            page: 1,
            limit: 10,
          },
        }),
      ).unwrap();

      console.log("✅ Links activos obtenidos del backend:", activosResult);
      setReferralLinksActivos(activosResult?.links || []);
      console.log(
        "✅ Links activos cargados:",
        activosResult?.links?.length || 0,
      );

      // Cargar links finalizados
      console.log("📡 Cargando links de referido finalizados...");
      const finalizadosResult = await dispatch(
        getReferralLinksDeUsuario({
          uid: resolvedUid,
          filters: {
            eventoEstado: "activo",
            eventoFechaHoraFinHasta: now,
            sortBy: "eventoFechaHoraFin",
            sortOrder: "desc",
            page: 1,
            limit: 10,
          },
        }),
      ).unwrap();

      setReferralLinksFinalizados(finalizadosResult?.links || []);
      console.log(
        "✅ Links finalizados cargados:",
        finalizadosResult?.links?.length || 0,
      );

      setTabsDataLoaded((prev) => ({ ...prev, eventos: true }));
    } catch (error) {
      console.error("❌ Error al cargar links de referido:", error);
    } finally {
      setLoadingReferralLinks(false);
    }
  };

  // Cargar favoritos públicos del usuario visitado
  const loadFavoritosPublicosData = async () => {
    if (favoritosLoaded || loadingFavoritosPublicos) return;
    setLoadingFavoritosPublicos(true);
    const resolvedUid = info?.uid || id;
    try {
      const [eventosResult, experienciasResult] = await Promise.all([
        dispatch(getFavoritosByUsuarioUid(resolvedUid)).unwrap(),
        dispatch(getFavoritasExperienciasByUid(resolvedUid)).unwrap(),
      ]);
      setFavoritosPublicos(Array.isArray(eventosResult) ? eventosResult : []);
      setExperienciasFavoritasPublicas(Array.isArray(experienciasResult) ? experienciasResult : []);
      setFavoritosLoaded(true);
    } catch (error) {
      console.error("Error al cargar favoritos públicos:", error);
      setFavoritosLoaded(true);
    } finally {
      setLoadingFavoritosPublicos(false);
    }
  };

  // Manejar seguir/dejar de seguir usuario
  const handleFollow = async () => {
    if (!isAuthenticated) return;
    setFollowLoading(true);
    try {
      if (isFollowing) {
        await dispatch(eliminarSeguidor({ uidSeguido: info?.uid })).unwrap();
        setIsFollowing(false);
        setTotalSeguidores((prev) => Math.max(0, prev - 1));
      } else {
        await dispatch(crearSeguidor({ uidSeguido: info?.uid })).unwrap();
        setIsFollowing(true);
        setTotalSeguidores((prev) => prev + 1);
      }
    } catch (error) {
      console.error("Error al cambiar estado de seguimiento:", error);
    } finally {
      setFollowLoading(false);
    }
  };

  // Manejar compartir perfil
  const handleSharePerfil = async () => {
    await shareContent({
      url: `/usuario/${info?.alias || info?.uid}`,
      title: `${info?.nombre || ""} ${info?.apellido || ""}`.trim() || "Perfil",
      text: `¡Conoce a ${info?.nombre || "este usuario"} en AppWhoop!`,
      successMessage: "¡Link del perfil copiado!",
    });
  };

  // Manejar cambio de tab
  const handleTabChange = (index) => {
    console.log("🔄 Cambiando a tab:", index);
    setActiveTab(index);

    // Determinar qué tab se activó basándonos en el index y la configuración de tabs
    const isAmbassador = info?.perEmbajador === 1;

    // Favoritos es siempre index 0; Eventos (embajador) queda en index 1; Mis Tickets al final
    if (index === 0) {
      loadFavoritosPublicosData();
    } else if (isAmbassador && index === 1 && !tabsDataLoaded.eventos) {
      loadEventosData();
    }
  };

  // Estados de carga individuales para cada sección
  const isLoadingPortada = !userInfoLoaded || userLoading;
  const isLoadingInfoPerfil = !userInfoLoaded || userLoading;

  // Construir tabs dinámicamente según si es embajador
  const tabs = [];

  // Favoritos siempre es el primer tab
  tabs.push({
    name: "Favoritos",
    icon: "fa-solid fa-heart",
    current: activeTab === 0,
  });

  // Eventos solo para embajadores (segundo tab)
  if (info?.perEmbajador === 1) {
    tabs.push({
      name: "Eventos",
      icon: "fa-solid fa-calendar-days",
      current: activeTab === 1,
    });
  }

  // Mis Tickets siempre al final
  tabs.push({
    name: "Mis Tickets",
    icon: "fa-solid fa-ticket",
    current: info?.perEmbajador === 1 ? activeTab === 2 : activeTab === 1,
  });

  const tabInfo = {
    title: "",
    description: "",
    layout: "horizontal",
    textColor: "text-white",
    textColorHover: "hover:text-aw-green",
    textColorActive: "text-aw-green",
    barTabActive: "bg-aw-green",
    bgColor: "bg-aw-bluedark",
    bgColorHover: "hover:bg-aw-bluedark-75",
    bgColorActive: "bg-aw-bluedark-50",
    borderColor: "border-aw-bluedark-50",
    borderColorActive: "aw-green",
    tabs: tabs,
  };

  // Construir contenido de las pestañas dinámicamente para garantizar sincronización con tabs
  const tabContents = [];

  // Favoritos es siempre el primer tab (índice 0)
  tabContents.push(
    <div
      key="favoritos"
      className="bg-aw-bluedark-95 rounded-lg p-4 min-h-[400px]"
    >
      <h3 className="text-xl font-semibold mb-4 text-white">Favoritos</h3>
      {loadingFavoritosPublicos ? (
        <SkeletonTheme baseColor="#1e293b" highlightColor="#334155">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((index) => (
              <div key={index}>
                <Skeleton height={180} borderRadius={8} className="mb-2" />
                <Skeleton height={16} className="mb-1" />
                <Skeleton height={14} width="60%" />
              </div>
            ))}
          </div>
        </SkeletonTheme>
      ) : (favoritosPublicos.length > 0 || experienciasFavoritasPublicas.length > 0) ? (
        <div className="space-y-6">
          {favoritosPublicos.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">Eventos</h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {favoritosPublicos.map((evento, index) => (
                  <a
                    key={evento.uid || index}
                    href={`/evento/${evento.uid}`}
                    className="group flex flex-col rounded-xl overflow-hidden bg-aw-bluedark border border-gray-700 hover:border-aw-green transition-colors"
                  >
                    <div className="w-full aspect-[3/4] overflow-hidden">
                      {evento.imgEvento ? (
                        <img src={evento.imgEvento} alt={evento.nombre} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                      ) : (
                        <div className="w-full h-full bg-aw-blueblack flex items-center justify-center">
                          <FontAwesomeIcon icon="fa-solid fa-calendar-days" className="text-4xl text-gray-600" />
                        </div>
                      )}
                    </div>
                    <div className="p-2">
                      <p className="text-white text-sm font-semibold line-clamp-2 mb-1">{evento.nombre}</p>
                      {evento.empresaNombre && <p className="text-gray-400 text-xs line-clamp-1">{evento.empresaNombre}</p>}
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}
          {experienciasFavoritasPublicas.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">Experiencias</h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {experienciasFavoritasPublicas.map((exp, index) => (
                  <a
                    key={exp.slug || index}
                    href={`/experiencias/${exp.slug}`}
                    className="group flex flex-col rounded-xl overflow-hidden bg-aw-bluedark border border-gray-700 hover:border-aw-green transition-colors"
                  >
                    <div className="w-full aspect-square overflow-hidden">
                      {exp.imagen ? (
                        <img src={exp.imagen} alt={exp.titulo} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                      ) : (
                        <div className="w-full h-full bg-aw-blueblack flex items-center justify-center">
                          <FontAwesomeIcon icon="fa-solid fa-compass" className="text-4xl text-gray-600" />
                        </div>
                      )}
                    </div>
                    <div className="p-2">
                      <p className="text-white text-sm font-semibold line-clamp-2">{exp.titulo}</p>
                      <p className="text-aw-green text-xs mt-1">Panorama</p>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-12 text-gray-400">
          <FontAwesomeIcon icon="fa-regular fa-heart" className="text-5xl mb-4 opacity-30" />
          <h4 className="text-lg font-semibold mb-2 text-white">Sin favoritos públicos</h4>
          <p className="text-center text-sm">Este usuario no tiene favoritos públicos aún.</p>
        </div>
      )}
    </div>,
  );

  // Eventos solo para embajadores (índice 1)
  if (info?.perEmbajador === 1) {
    tabContents.push(
      <div
        key="eventos"
        className="bg-aw-bluedark-95 rounded-lg py-4 min-h-[400px]"
      >
        {loadingReferralLinks ? (
          <SkeletonTheme baseColor="#1e293b" highlightColor="#334155">
            <div className="flex gap-4 overflow-x-auto scrollbar-hide">
              {[1, 2, 3, 4, 5].map((index) => (
                <div key={index} className="min-w-[200px] max-w-[200px]">
                  <Skeleton height={208} className="mb-2" borderRadius={8} />
                  <div className="px-2 py-1">
                    <Skeleton height={20} className="mb-1" />
                    <Skeleton height={14} />
                  </div>
                  <Skeleton height={36} className="mt-2" />
                </div>
              ))}
            </div>
          </SkeletonTheme>
        ) : (
          <>
            {/* Eventos Activos */}
            <div>
              <h3 className="text-xl font-semibold mb-4 pl-4 text-white">
                Eventos Activos
              </h3>
              {referralLinksActivos.length > 0 ? (
                <TicketCarousel
                  tickets={referralLinksActivos}
                  TicketItem={TicketCardReferralLink}
                  sectionType="referralLinksActivos"
                  uid={id}
                  onLoadMore={(newLinks) => {
                    console.log(
                      "📥 onLoadMore Activos - Links recibidos:",
                      newLinks,
                    );
                    setReferralLinksActivos((prev) => [...prev, ...newLinks]);
                  }}
                />
              ) : (
                <p className="text-gray-400 text-center py-8">
                  No hay eventos activos en este momento
                </p>
              )}
            </div>

            {/* Eventos Finalizados */}
            <div>
              <h3 className="text-xl font-semibold mb-4 pl-4 text-white">
                Eventos Finalizados
              </h3>
              {referralLinksFinalizados.length > 0 ? (
                <TicketCarousel
                  tickets={referralLinksFinalizados}
                  TicketItem={TicketCardReferralLink}
                  sectionType="referralLinksFinalizados"
                  uid={id}
                  onLoadMore={(newLinks) => {
                    console.log(
                      "📥 onLoadMore Finalizados - Links recibidos:",
                      newLinks,
                    );
                    setReferralLinksFinalizados((prev) => [
                      ...prev,
                      ...newLinks,
                    ]);
                  }}
                />
              ) : (
                <p className="text-gray-400 text-center py-8">
                  No hay eventos finalizados
                </p>
              )}
            </div>
          </>
        )}
      </div>,
    );
  }

  // Mis Tickets va al final
  tabContents.push(
    <div
      key="misTickets"
      className="bg-aw-bluedark-95 rounded-lg py-4 min-h-[400px]"
    >
      {loadingEventosPasados ? (
        <div className="text-center text-white">
          <SkeletonTheme baseColor="#1e293b" highlightColor="#334155">
            <Skeleton height={30} width={200} className="mb-4 mx-auto" />
            <div className="flex gap-4 overflow-x-auto scrollbar-hide">
              {[1, 2, 3, 4, 5].map((index) => (
                <div key={index} className="min-w-[200px] max-w-[200px]">
                  <Skeleton height={208} className="mb-2" borderRadius={8} />
                  <div className="px-2 py-1">
                    <Skeleton height={20} className="mb-1" />
                    <Skeleton height={14} />
                  </div>
                  <Skeleton height={36} className="mt-2" />
                </div>
              ))}
            </div>
          </SkeletonTheme>
        </div>
      ) : (
        <div>
          <h3 className="text-xl font-semibold mb-4 pl-4 text-white">
            Eventos Pasados
          </h3>
          {eventosPasados.length > 0 ? (
            <TicketCarousel
              tickets={eventosPasados}
              TicketItem={TicketCardEventoPublico}
              sectionType="eventosPasados"
              uid={id}
              onLoadMore={(newEventos) => {
                setEventosPasados((prev) => [...prev, ...newEventos]);
              }}
            />
          ) : (
            <p className="text-gray-400 text-center py-8">
              No hay eventos pasados para mostrar
            </p>
          )}
        </div>
      )}
    </div>,
  );

  console.log("[referralLinksActivos]:", referralLinksActivos);
  console.log("[tabContents length]:", tabContents.length);
  console.log("[tabs length]:", tabs.length);

  // Badge de embajador activo
  const isEmbajador = info?.perEmbajador === 1;
  const embajadorActivo = info?.ambassadorActive;

  return (
    <>
      {/* PORTADA — ancho máximo centrado */}
      <div className="w-full max-w-5xl mx-auto relative">
        {isLoadingPortada ? (
          <SkeletonTheme baseColor="#1e293b" highlightColor="#334155">
            <Skeleton height={320} className="w-full rounded-b-3xl" />
          </SkeletonTheme>
        ) : (
          <Portada info={info} />
        )}
      </div>

      {/* SECCIÓN PRINCIPAL — ancho completo, fondo oscuro */}
      <div
        className={`w-full bg-aw-blueblack text-aw flex flex-col items-center rounded-t-[8%] pt-20 -mt-20 sm:-mt-[5.5rem] ${
          info?.empresas?.length > 0 ? "" : "min-h-screen"
        }`}
      >
        {/* HEADER: avatar solapando portada + info + redes + seguir */}
        <div className="w-full max-w-5xl mx-auto px-4 mb-6 sm:mt-10">
          {isLoadingInfoPerfil ? (
            <SkeletonTheme baseColor="#1e293b" highlightColor="#334155">
              <div className="flex flex-row items-end gap-4 mt-2">
                <Skeleton circle height={112} width={112} />
                <div className="flex flex-col gap-2 mb-4">
                  <Skeleton height={28} width={200} borderRadius={8} />
                  <Skeleton height={16} width={120} borderRadius={8} />
                  <Skeleton height={14} width={80} borderRadius={8} />
                </div>
              </div>
            </SkeletonTheme>
          ) : (
            <div className="flex flex-col md:flex-row gap-3 items-center">

              {/* Fila 1: Avatar (izquierda) + Nombre/badges (derecha) — siempre en fila */}
              <div className="flex flex-row items-center gap-3 sm:-mt-10">

                {/* Avatar — solapando borde inferior de portada */}
                <motion.div
                  custom={0}
                  variants={fadeUp}
                  initial="hidden"
                  animate="visible"
                  className="relative shrink-0"
                >
                  <div className="w-20 h-20 sm:w-[100px] sm:h-[100px] rounded-full ring-4 ring-aw-green ring-offset-2 ring-offset-aw-blueblack overflow-hidden shadow-xl shadow-black/50">
                    <img
                      src={info?.imgPerfil || genericAvatar}
                      alt={`${info?.nombre || ""} ${info?.apellido || ""}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  {isEmbajador && embajadorActivo && (
                    <span className="absolute bottom-1 right-1 w-3 h-3 bg-aw-green rounded-full border-2 border-aw-blueblack" title="Embajador activo" />
                  )}
                </motion.div>

                {/* Nombre + alias + badges */}
                <motion.div
                  custom={1}
                  variants={fadeUp}
                  initial="hidden"
                  animate="visible"
                  className="flex-1 min-w-0"
                >
                  <h1 className="text-white text-lg sm:text-2xl font-bold leading-tight">
                    {info?.nombre} {info?.apellido}
                  </h1>
                  <p className="text-gray-400 text-sm mt-0.5">
                    @{info?.alias || info?.uid?.slice(0, 8)}
                  </p>
                  <div className="flex flex-wrap items-center gap-2 mt-1.5">
                    <span className="inline-flex items-center gap-1 text-aw-green text-xs font-medium">
                      <FontAwesomeIcon icon="fa-solid fa-certificate" className="text-[10px]" />
                      Perfil verificado
                    </span>
                    {isEmbajador && (
                      <span className="inline-flex items-center gap-1 bg-aw-green/15 text-aw-green border border-aw-green/30 rounded-full px-2 py-0.5 text-xs font-semibold">
                        <FontAwesomeIcon icon="fa-solid fa-star" className="text-[10px]" />
                        Embajador
                      </span>
                    )}
                  </div>
                </motion.div>

              </div>

              {/* Fila 2: Redes sociales */}
              {redesSociales.length > 0 && (
                <motion.div
                  custom={2}
                  variants={fadeUp}
                  initial="hidden"
                  animate="visible"
                  className="flex flex-wrap gap-1.5 justify-center"
                >
                  {redesSociales.map((rrss, index) => (
                    <motion.a
                      key={index}
                      href={rrss.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      whileHover={{ scale: 1.12 }}
                      whileTap={{ scale: 0.92 }}
                      className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-aw-bluedark border border-gray-600 hover:border-aw-green hover:bg-aw-green/10 flex items-center justify-center text-white transition-colors shadow-md"
                    >
                      <FontAwesomeIcon icon={rrss.icon} className="text-xs sm:text-sm" />
                    </motion.a>
                  ))}
                </motion.div>
              )}

              {/* Fila 3: Stats (seguidores | seguidos) + botón acción */}
              <motion.div
                custom={3}
                variants={fadeUp}
                initial="hidden"
                animate="visible"
                className="flex items-center gap-3 flex-wrap justify-center"
              >
                {/* Seguidores */}
                <div className="flex items-baseline gap-1">
                  <span className="text-white font-bold text-sm sm:text-base">{totalSeguidores}</span>
                  <span className="text-gray-400 text-xs">
                    {totalSeguidores === 1 ? "seguidor" : "seguidores"}
                  </span>
                </div>

                {/* Separador */}
                {(info?.totalSeguidos !== undefined) && (
                  <>
                    <div className="w-px h-3.5 bg-gray-600" />
                    <div className="flex items-baseline gap-1">
                      <span className="text-white font-bold text-sm sm:text-base">{info.totalSeguidos}</span>
                      <span className="text-gray-400 text-xs">seguidos</span>
                    </div>
                  </>
                )}

                {/* Separador antes del botón */}
                <div className="w-px h-3.5 bg-gray-600" />

                {/* Botón: Seguir (si es otro usuario) o Compartir Perfil (si es el propio o no autenticado) */}
                {isAuthenticated && user?.uid !== info?.uid ? (
                  <AnimatePresence mode="wait">
                    <motion.button
                      key={isFollowing ? "siguiendo" : "seguir"}
                      onClick={handleFollow}
                      disabled={followLoading}
                      whileTap={{ scale: 0.93 }}
                      className={`px-4 py-1.5 rounded-lg font-semibold text-xs sm:text-sm transition-all border whitespace-nowrap ${
                        isFollowing
                          ? "bg-transparent border-aw-green text-aw-green hover:bg-aw-green/10"
                          : "bg-aw-green border-aw-green text-aw-bluedark hover:brightness-110"
                      } ${followLoading ? "opacity-60 cursor-not-allowed" : ""}`}
                    >
                      {followLoading ? (
                        <i className="fas fa-spinner fa-spin mr-1" />
                      ) : (
                        <FontAwesomeIcon
                          icon={isFollowing ? "fa-solid fa-user-check" : "fa-solid fa-user-plus"}
                          className="mr-1.5"
                        />
                      )}
                      {isFollowing ? "Siguiendo" : "Seguir"}
                    </motion.button>
                  </AnimatePresence>
                ) : (
                  <motion.button
                    onClick={handleSharePerfil}
                    whileHover={{ scale: 1.04 }}
                    whileTap={{ scale: 0.93 }}
                    className="px-4 py-1.5 rounded-lg font-semibold text-xs sm:text-sm border border-gray-600 text-white hover:border-aw-green hover:bg-aw-green/10 transition-all whitespace-nowrap"
                  >
                    <FontAwesomeIcon icon="fa-solid fa-share-nodes" className="mr-1.5" />
                    Compartir Perfil
                  </motion.button>
                )}
              </motion.div>

            </div>
          )}
        </div>

        {/* Skeleton de tabs */}
        {isLoadingPortada && (
          <div className="w-full px-4">
            <SkeletonTheme baseColor="#1e293b" highlightColor="#334155">
              <div className="flex w-full gap-2">
                <Skeleton height={44} width={300} borderRadius={8} />
              </div>
            </SkeletonTheme>
          </div>
        )}

        {/* Tabs — ancho completo */}
        <div className="w-full pb-8">
          <Tab tabInfo={tabInfo} onTabChange={handleTabChange}>
            {tabContents}
          </Tab>
        </div>
      </div>
    </>
  );
}
